import type { Locale } from "@/lib/i18n";

/**
 * Auth-funnel copy (signup, login, password reset) in both locales.
 *
 * WHY THESE THREE AND NOT THE DASHBOARD. The public surface is translated, so a Kreyòl visitor
 * reads the landing page, the FAQ, the tutorials and the legal documents in Kreyòl — and then hit
 * an English signup form the moment they pressed "Kòmanse". Translating signup alone would only
 * MOVE that seam, because signup links to login and back. The funnel is translated as a unit; the
 * signed-in dashboard stays English by the scope decision of 2026-08-15 (public pages + legal).
 *
 * ERROR STRINGS ARE INCLUDED DELIBERATELY. A form that greets you in Kreyòl and then refuses you
 * in English is worse than one that was never translated: the reader cannot tell whether they made
 * a mistake or the site broke, and this is the exact moment they are deciding whether to trust it
 * with an exchange key.
 *
 * Official 1979 IPN orthography — rules in content/ui.ts.
 */

export type AuthDict = {
  loading: string;
  /** aria-label on the reveal toggle. Shared across signup/login/reset — the control is the
   *  same everywhere, and duplicating it per section invites the two copies to drift. */
  showPassword: string;
  hidePassword: string;
  signup: {
    lead: string;
    email: string;
    emailPlaceholder: string;
    password: string;
    confirm: string;
    /** Optional: the code of the person who invited them (`twokeio-…`). */
    referral: string;
    submit: string;
    submitting: string;
    haveAccount: string;
    signIn: string;
    errShort: string;
    errMismatch: string;
    errGeneric: string;
  };
  login: {
    lead: string;
    email: string;
    password: string;
    forgot: string;
    submit: string;
    submitting: string;
    codeCta: string;
    codeSending: string;
    codeLabel: string;
    verify: string;
    verifying: string;
    noAccount: string;
    createOne: string;
    errNoEmail: string;
    resetSent: string;
    useCode: string;
    backToPassword: string;
  };
  reset: {
    title: string;
    lead: string;
    code: string;
    password: string;
    confirm: string;
    submit: string;
    saving: string;
    errCode: string;
    errShort: string;
    errMismatch: string;
    backToLogin: string;
  };
};

const en: AuthDict = {
  loading: "Loading…",
  showPassword: "Show password",
  hidePassword: "Hide password",
  signup: {
    lead: "Create your account — email + password. You can add 2FA when you connect a key.",
    email: "Email",
    emailPlaceholder: "you@example.com",
    password: "Password (min 12 chars)",
    confirm: "Confirm password",
    referral: "Referral code (optional)",
    submit: "Create account",
    submitting: "Creating…",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    errShort: "Password must be at least 12 characters.",
    errMismatch: "Passwords don’t match.",
    errGeneric: "could not create account",
  },
  login: {
    lead: "Sign in to your account.",
    email: "Email",
    password: "Password",
    forgot: "Forgot password?",
    submit: "Sign in",
    submitting: "Signing in…",
    codeCta: "Email me a sign-in code",
    codeSending: "Sending…",
    codeLabel: "6-digit code",
    verify: "Verify",
    verifying: "Verifying…",
    noAccount: "No account?",
    createOne: "Create one",
    errNoEmail: "Enter your email first.",
    resetSent:
      "If that email has an account, a reset link and a 6-digit code are on their way — you need both.",
    useCode: "Use an email code instead",
    backToPassword: "Back to password",
  },
  reset: {
    title: "Reset your password",
    lead: "Enter the 6-digit code from the second email.",
    code: "6-digit code",
    password: "New password (min 12 chars)",
    confirm: "Confirm password",
    submit: "Set new password",
    saving: "Saving…",
    errCode: "Enter the 6-digit code from the second email.",
    errShort: "Password must be at least 12 characters.",
    errMismatch: "Passwords don’t match.",
    backToLogin: "Back to sign in",
  },
};

const ht: AuthDict = {
  loading: "L ap chaje…",
  showPassword: "Montre modpas",
  hidePassword: "Kache modpas",
  signup: {
    lead: "Kreye kont ou — imel ak modpas. Ou ka ajoute 2FA lè w ap konekte yon kle.",
    email: "Imel",
    emailPlaceholder: "ou@egzanp.com",
    password: "Modpas (omwen 12 karaktè)",
    confirm: "Konfime modpas la",
    referral: "Kòd parenaj (si w genyen)",
    submit: "Kreye kont lan",
    submitting: "L ap kreye…",
    haveAccount: "Ou gen yon kont deja?",
    signIn: "Konekte",
    errShort: "Modpas la dwe gen omwen 12 karaktè.",
    errMismatch: "De modpas yo pa menm.",
    errGeneric: "nou pa t ka kreye kont lan",
  },
  login: {
    lead: "Konekte sou kont ou.",
    email: "Imel",
    password: "Modpas",
    forgot: "Ou bliye modpas ou?",
    submit: "Konekte",
    submitting: "L ap konekte…",
    codeCta: "Voye yon kòd koneksyon ban mwen pa imel",
    codeSending: "L ap voye…",
    codeLabel: "Kòd 6 chif",
    verify: "Verifye",
    verifying: "L ap verifye…",
    noAccount: "Ou pa gen kont?",
    createOne: "Kreye youn",
    errNoEmail: "Mete imel ou anvan.",
    resetSent:
      "Si imel sa a gen yon kont, yon lyen reyinisyalizasyon ak yon kòd 6 chif ap rive — ou bezwen toude.",
    useCode: "Konekte ak yon kòd pa imel pito",
    backToPassword: "Retounen nan modpas la",
  },
  reset: {
    title: "Reyinisyalize modpas ou",
    lead: "Mete kòd 6 chif ki nan dezyèm imel la.",
    code: "Kòd 6 chif",
    password: "Nouvo modpas (omwen 12 karaktè)",
    confirm: "Konfime modpas la",
    submit: "Anrejistre nouvo modpas la",
    saving: "L ap anrejistre…",
    errCode: "Mete kòd 6 chif ki nan dezyèm imel la.",
    errShort: "Modpas la dwe gen omwen 12 karaktè.",
    errMismatch: "De modpas yo pa menm.",
    backToLogin: "Tounen nan koneksyon an",
  },
};

const DICTS: Record<Locale, AuthDict> = { en, ht };

export const getAuth = (locale: Locale): AuthDict => DICTS[locale] ?? en;
